import { PDFBarHeight, zIndex } from "@nova/constants"
import * as Icons from "@nova/component-icons"
import {
    leftLogoDark,
    leftLogoDefault,
    PageSizes,
    pageSizes,
    selectColorTheme,
    selectData,
    selectFilename,
    selectLegendPosition,
    selectOrientation,
    selectPageSize,
    setColorTheme,
    setData,
    setFilename,
    setLegendPosition,
    setOrientation,
    setPageSize,
    setSnapshotOpen,
} from "../PDFSlice"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import {
    ButtonWorkspaceDropdown,
    ButtonRadioDropdown,
    ButtonPrimary,
    ButtonStatus,
} from "@nova/component-btn"
import { useCallback, useState } from "react"
import { SegmentedControl } from "panels/content/top-bar/TODO-put-in-package/SegmentedControl"
import { WorkspaceTextInput } from "@nova/component-inline-text-input"
import { formatShortDate } from "@nova/format"
import { setSidePanelMode } from "panels/content/side-panel/slice.new"
import { setActiveTool } from "plugins/Toolbox/tool-registry"

export const PDFTopBar = ({
    onSave,
    loading,
}: {
    onSave: () => Promise<void>
    loading: boolean
}) => {
    const dispatch = useAppDispatch()
    const [openDropdownMenuId, setOpenDropdownMenuId] = useState<string | undefined>()

    const filename = useAppSelector(selectFilename())
    const [hasEditedName, setHasEditedName] = useState(false)
    return (
        <div
            className="fixed left-0 top-0 w-screen flex bg-gray-800 p-4 items-center justify-between"
            style={{ height: PDFBarHeight + "px", zIndex: zIndex.PDFTopBar }}
        >
            <div className="flex items-center">
                <Icons.Icon
                    Icon={Icons.XClose}
                    className="text-gray-300 cursor-pointer hover:text-gray-100"
                    onClick={() => {
                        dispatch(setSnapshotOpen(false))
                        dispatch(setActiveTool(undefined))
                    }}
                />
                <PDFTopBarDivider />
                <Icons.Icon Icon={Icons.Map} className="text-gray-300 mr-[7px]" />
                <h4 className="text-gray-0">Generate PDF</h4>
                <PDFTopBarDivider />
                <div className="flex items-center gap-6">
                    <PDFSizeDropdown
                        activeDropdownId={openDropdownMenuId}
                        setActiveDropdownId={setOpenDropdownMenuId}
                    />
                    <PDFColorThemeSwitcher />
                    <PDFLayoutDropdown />
                </div>
            </div>
            <div className="flex items-center">
                <small className="text-gray-100 mr-2">Name</small>
                <WorkspaceTextInput
                    name="pdf-filename"
                    className="!h-[2rem] mr-2"
                    inputClassName="!text-gray-0 !h-[2rem] self-center !text-[14px]"
                    value={
                        !filename && !hasEditedName
                            ? `${formatShortDate({ value: new Date() }).replaceAll("/", "_")}_PDF`
                            : filename
                    }
                    handleChange={(value) => {
                        if (!hasEditedName) setHasEditedName(true)
                        dispatch(setFilename(value))
                    }}
                    isClearable={false}
                />
                <ButtonPrimary
                    RightIcon={Icons.Checkmark}
                    title="Generate"
                    onClick={async () => {
                        try {
                            await onSave()
                            dispatch(setSidePanelMode({ mode: "PDFs" }))
                        } catch (err) {
                            console.error(err)
                        }
                    }}
                    status={loading ? ButtonStatus.Loading : ButtonStatus.Normal}
                    spinnerColors={{ primary: "#27BBFF", background: "#FFFFFF" }} // blue-500, white
                    preserveWidthWhileLoading
                />
            </div>
        </div>
    )
}

const PDFSizeDropdown = ({
    activeDropdownId,
    setActiveDropdownId,
}: {
    activeDropdownId?: string
    setActiveDropdownId: (id?: string) => void
}) => {
    const dispatch = useAppDispatch()
    const size = useAppSelector(selectPageSize())
    const dropdownId = "pdf-size"
    const menuOpen = activeDropdownId === dropdownId
    return (
        <div className="flex items-center">
            <small className="text-gray-200 mr-3">Size</small>
            <ButtonWorkspaceDropdown
                label={size}
                menuOpen={menuOpen}
                setMenuOpen={(isOpen) => {
                    if (isOpen) setActiveDropdownId(dropdownId)
                    else setActiveDropdownId(undefined)
                }}
                dropdownButtons={(
                    Object.values(PageSizes).filter((v) => typeof v === "number") as PageSizes[]
                ).map((pageSize) => ({
                    label: pageSizes[pageSize],
                    onClick: () => dispatch(setPageSize(pageSize)),
                }))}
                widthClass="w-[130px]"
                isSmall
            />
        </div>
    )
}

const PDFColorThemeSwitcher = () => {
    const dispatch = useAppDispatch()
    const colorTheme = useAppSelector(selectColorTheme())
    const logo = useAppSelector(selectData("leftLogo"))

    const changeColorTheme = useCallback(
        (index: number) => {
            //if the PDF is using the Nova logo, switch it to the other variant

            if (index === 0) {
                dispatch(setColorTheme("light"))
                if (logo === leftLogoDark)
                    dispatch(setData({ type: "leftLogo", data: leftLogoDefault }))
            } else {
                dispatch(setColorTheme("dark"))
                if (logo === leftLogoDefault)
                    dispatch(setData({ type: "leftLogo", data: leftLogoDark }))
            }
        },
        [logo, dispatch]
    )

    return (
        <div className="flex items-center">
            <small className="text-gray-200 mr-3">Color</small>
            <div className="w-[80px]">
                <SegmentedControl
                    segments={[Icons.Sun, Icons.Moon]}
                    defaultSelectedIndex={colorTheme === "light" ? 0 : 1}
                    onChange={changeColorTheme}
                />
            </div>
        </div>
    )
}

const PDFLayoutDropdown = () => {
    const [isOpen, setIsOpen] = useState(false)
    const dispatch = useAppDispatch()
    const orientation = useAppSelector(selectOrientation())
    const legendPosition = useAppSelector(selectLegendPosition())

    return (
        <ButtonRadioDropdown
            label="Layout"
            menuOpen={isOpen}
            setMenuOpen={setIsOpen}
            labelIcon={Icons.Layout}
            sections={[
                {
                    title: "Orientation",
                    options: [
                        {
                            label: "Landscape",
                            onClick: () => dispatch(setOrientation("Landscape")),
                            isSelected: orientation === "Landscape",
                        },
                        {
                            label: "Portrait",
                            onClick: () => dispatch(setOrientation("Portrait")),
                            isSelected: orientation === "Portrait",
                        },
                    ],
                },
                {
                    title: "Legend",
                    options: [
                        {
                            label: "Right",
                            onClick: () => dispatch(setLegendPosition("right")),
                            isSelected: legendPosition === "right",
                        },
                        {
                            label: "Bottom",
                            onClick: () => dispatch(setLegendPosition("bottom")),
                            isSelected: legendPosition === "bottom",
                        },
                    ],
                },
            ]}
            isSmall
        />
    )
}

const PDFTopBarDivider = () => {
    return <div className="border-r border-gray-700 pl-[18px] mr-[18px] h-6" />
}
